import { getLocalizedPlatformName } from "./platformNames";

/**
 * Localize free-text / English order update reasons for notifications & timeline.
 */
export function localizeOrderReason(reason, t) {
  if (!reason || typeof reason !== "string") return "";

  const translateReasonText = (reasonText) => {
    if (!reasonText || typeof reasonText !== "string") return reasonText;
    const reasonLower = reasonText.trim().toLowerCase().replace(/\.$/, "");
    const reasonTextMap = {
      "customer canceled the order": "reasonText.customer_canceled_the_order",
      "customer cancelled the order": "reasonText.customer_canceled_the_order",
      "customer rejected the order": "reasonText.customer_rejected_the_order",
      "customer requests order cancellation":
        "reasonText.customer_requests_cancellation",
      "out of stock": "reasonText.out_of_stock",
      "item not available": "reasonText.item_not_available",
      "store closed": "reasonText.store_closed",
      "preparation time exceeded": "reasonText.preparation_time_exceeded",
      "restaurant did not respond in time":
        "reasonText.restaurant_did_not_respond",
      "order rejected by merchant": "reasonText.order_rejected_by_merchant",
      "order cancelled by merchant": "reasonText.order_cancelled_by_merchant",
      "order canceled by merchant": "reasonText.order_cancelled_by_merchant",
      "order rejected by restaurant": "reasonText.order_rejected_by_restaurant",
      "order cancelled by restaurant": "reasonText.order_cancelled_by_restaurant",
      "order canceled by restaurant": "reasonText.order_cancelled_by_restaurant",
      "auto-accept failed": "orderNotification.autoAcceptFailedShort",
      "auto accept failed": "orderNotification.autoAcceptFailedShort",
    };
    const key = reasonTextMap[reasonLower];
    return key ? t(key) : reasonText;
  };

  const normalizePlatform = (name) => {
    const localized = getLocalizedPlatformName(name, t);
    if (localized) return localized;
    const l = String(name || "")
      .trim()
      .replace(/\.$/, "")
      .toLowerCase();
    if (l === "merchant") return t("actor.merchant");
    if (l === "restaurant") return t("actor.restaurant");
    return String(name || "").trim();
  };

  const patterns = [
    {
      regex: /^Customer cancel(?:led|ed) the order\.?$/i,
      key: "reason.customer.cancelled",
    },
    { regex: /^Order cancelled by customer\.?$/i, key: "reason.customer.cancelled" },
    {
      regex: /^Customer requests order cancellation\.?$/i,
      key: "reasonText.customer_requests_cancellation",
    },
    {
      regex: /^Restaurant did not respond in time\.?$/i,
      key: "reasonText.restaurant_did_not_respond",
    },
    { regex: /^Order rejected by merchant\.?$/i, key: "reasonText.order_rejected_by_merchant" },
    {
      regex: /^Order cancel(?:led|ed) by merchant\.?$/i,
      key: "reasonText.order_cancelled_by_merchant",
    },
    {
      regex: /^Order rejected by restaurant\.?$/i,
      key: "reasonText.order_rejected_by_restaurant",
    },
    {
      regex: /^Order cancel(?:led|ed) by restaurant\.?$/i,
      key: "reasonText.order_cancelled_by_restaurant",
    },
    { regex: /^Out of stock\.?$/i, key: "reasonText.out_of_stock" },
    { regex: /^Item not available\.?$/i, key: "reasonText.item_not_available" },
    { regex: /^Store closed\.?$/i, key: "reasonText.store_closed" },
    {
      regex: /^Preparation time exceeded\.?$/i,
      key: "reasonText.preparation_time_exceeded",
    },
    {
      regex: /^Order created via Digital Catalog\.?$/i,
      key: "reasonText.order_created_via_digital_catalog",
    },
    {
      regex: /^Order created via Order Agent\.?$/i,
      key: "reasonText.order_created_via_order_agent",
    },
    {
      regex: /^Order created via (Chat Agent|AI Order Agent)\.?$/i,
      key: "reasonText.order_created_via_chat_agent",
    },
    { regex: /^Order has been rejected by user$/i, key: "reason.user.rejected" },
    { regex: /^Order has been cancelled by user$/i, key: "reason.user.cancelled" },
    { regex: /^Order has been accepted by user$/i, key: "reason.user.accepted" },
    {
      regex: /^Order has been marked as ready for delivery by user$/i,
      key: "reason.user.ready_for_delivery",
    },
    {
      regex: /^Order has been marked as delivered by user$/i,
      key: "reason.user.delivered",
    },
    {
      regex: /^Order status updated to (.+) by user$/i,
      key: "reason.user.status_updated",
      vars: (m) => ({ status: localizeOrderStatus(m[1], t) }),
    },
    {
      regex: /^Order received from\s+([^:]+)(?::\s+(.+))?$/i,
      key: "reason.webhook.received_from_platform",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Order has been rejected by ([^:]+)(?::\s+(.+))?$/i,
      key: "reason.webhook.rejected",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Order has been cancelled by ([^:]+)(?::\s+(.+))?$/i,
      key: "reason.webhook.cancelled",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Order has been accepted by ([^:]+)(?::\s+(.+))?$/i,
      key: "reason.webhook.accepted",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Order has been marked as ready for delivery by ([^:]+)(?::\s+(.+))?$/i,
      key: "reason.webhook.ready_for_delivery",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Order is out for delivery \(([^)]+)\)(?::\s+(.+))?$/i,
      key: "reason.webhook.out_for_delivery",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Driver assigned to order (\d+) \(([^)]+)\)$/i,
      key: "reason.webhook.driver_assigned_to_order",
      vars: (m) => ({ orderId: m[1], platform: normalizePlatform(m[2]) }),
    },
    {
      regex: /^Driver assigned \(([^)]+)\)$/i,
      key: "reason.webhook.driver_assigned",
      vars: (m) => ({ platform: normalizePlatform(m[1]) }),
    },
    {
      regex: /^Order has been delivered \(([^)]+)\)(?::\s+(.+))?$/i,
      key: "reason.webhook.delivered",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Order timed out \(([^)]+)\)(?::\s+(.+))?$/i,
      key: "reason.webhook.timed_out",
      vars: (m) => ({ platform: normalizePlatform(m[1]), reason: m[2] || "" }),
    },
    {
      regex: /^Order status updated to (.+) by ([^:]+)(?::\s+(.+))?$/i,
      key: "reason.webhook.status_updated",
      vars: (m) => ({
        status: localizeOrderStatus(m[1], t),
        platform: normalizePlatform(m[2]),
        reason: m[3] || "",
      }),
    },
    {
      regex: /^Order has been automatically accepted by system$/i,
      key: "reason.system.auto_accepted",
    },
    {
      regex: /^Order has been automatically timed out due to no response$/i,
      key: "reason.system.auto_timed_out",
    },
    {
      regex: /^Order timed out due to no response\s*\??\s*$/i,
      key: "reason.system.timed_out_no_response",
    },
    {
      regex: /^Order Timed Out by System, not responding to it\.?$/i,
      key: "reason.system.auto_timed_out",
    },
    { regex: /^Auto accepted by system$/i, key: "reason.system.auto_accepted" },
    {
      regex: /^Auto time-out due to no response$/i,
      key: "reason.system.auto_timed_out",
    },
    { regex: /^Order has been rejected$/i, key: "reason.system.rejected" },
    { regex: /^Order has been cancelled$/i, key: "reason.system.cancelled" },
    { regex: /^Order has been accepted$/i, key: "reason.system.accepted" },
    {
      regex: /^Order status updated to (.+)$/i,
      key: "reason.system.status_updated",
      vars: (m) => ({ status: localizeOrderStatus(m[1], t) }),
    },
  ];

  for (const p of patterns) {
    const m = reason.match(p.regex);
    if (m) {
      const vars = p.vars ? p.vars(m) : undefined;
      if (vars?.reason && String(vars.reason).trim()) {
        vars.reason = translateReasonText(vars.reason);
      }
      const keyToUse =
        vars?.reason && String(vars.reason).trim()
          ? `${p.key}_with_reason`
          : p.key;
      return t(keyToUse, vars);
    }
  }

  return translateReasonText(reason);
}

/** Localize order status codes for notification banners. */
export function localizeOrderStatus(status, t) {
  if (!status) return "";
  const raw = String(status).trim();
  const upper = raw.toUpperCase();
  const statusKey = `statusText.${upper}`;
  const viaStatusText = t(statusKey, { defaultValue: "" });
  if (viaStatusText && viaStatusText !== statusKey) return viaStatusText;
  const viaDirect = t(upper, { defaultValue: "" });
  if (viaDirect && viaDirect !== upper) return viaDirect;
  return raw;
}
